import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/anmelden';
    }
    return Promise.reject(error);
  }
);

// ========================================
// AUTH
// ========================================
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getCurrentUser: () => api.get('/api/users/me'),
  completeOnboarding: () => api.post('/api/users/complete-onboarding'),
};

// ========================================
// PROFILES
// ========================================
export const profileAPI = {
  getMyProfile: () => api.get('/api/profiles/me'),
  updateMyProfile: (data) => api.put('/api/profiles/me', data),
  getProfile: (userId) => api.get(`/api/profiles/${userId}`),
  listAnbieter: (params) => api.get('/api/anbieter', { params }),
};

// ========================================
// WALDFLAECHEN (MY FOREST)
// ========================================
export const waldflaechenAPI = {
  create: (data) => api.post('/api/waldflaechen', data),
  list: () => api.get('/api/waldflaechen'),
  get: (id) => api.get(`/api/waldflaechen/${id}`),
  delete: (id) => api.delete(`/api/waldflaechen/${id}`),
};

// ========================================
// PROJECTS
// ========================================
export const projectAPI = {
  create: (data) => api.post('/api/projekte', data),
  list: (params) => api.get('/api/projekte', { params }),
  get: (id) => api.get(`/api/projekte/${id}`),
  updateStatus: (id, status) => api.put(`/api/projekte/${id}/status?new_status=${status}`),
};

// ========================================
// ANGEBOTE (OFFERS)
// ========================================
export const angeboteAPI = {
  submit: (projectId, data) => api.post(`/api/projekte/${projectId}/angebote`, data),
  listForProject: (projectId) => api.get(`/api/projekte/${projectId}/angebote`),
  listMyOffers: () => api.get('/api/meine-angebote'),
  accept: (angebotId) => api.put(`/api/angebote/${angebotId}/accept`),
};

// ========================================
// REVIEWS
// ========================================
export const reviewAPI = {
  create: (projectId, data) => api.post(`/api/projekte/${projectId}/reviews`, data),
  getForUser: (userId) => api.get(`/api/reviews/user/${userId}`),
  getForProject: (projectId) => api.get(`/api/reviews/project/${projectId}`),
};

// ========================================
// MESSAGES
// ========================================
export const messageAPI = {
  send: (data) => api.post('/api/messages', data),
  list: () => api.get('/api/messages'),
  getConversations: () => api.get('/api/messages/conversations'),
};

// ========================================
// DASHBOARD
// ========================================
export const dashboardAPI = {
  getStats: () => api.get('/api/dashboard/stats'),
};

// ========================================
// CONSTANTS
// ========================================
export const constantsAPI = {
  getBundeslaender: () => api.get('/api/constants/bundeslaender'),
  getKategorien: () => api.get('/api/constants/kategorien'),
  getBaumarten: () => api.get('/api/constants/baumarten'),
};

// ========================================
// AI ASSISTANT
// ========================================
export const assistantAPI = {
  chat: (message, sessionId) => api.post('/api/assistant/chat', { message, session_id: sessionId }),
};

// ========================================
// FALLANALYSE (Produktkern)
// ========================================
export const fallanalyseAPI = {
  nutzeransicht: (freitext, strukturiert = null, zielgruppe = 'waldbesitzer', tier = 'basis') =>
    api.post('/api/cases/nutzeransicht', { freitext, strukturiert, zielgruppe, tier }),
  projektEntwurf: (freitext, zielgruppe, ansicht) =>
    api.post('/api/cases/projekt-entwurf', { freitext, zielgruppe, ansicht }),
  projektEntwurfAbrufen: (id) =>
    api.get(`/api/cases/projekt-entwurf/${id}`),
  projektEntwurfZuordnen: (entwurfId, userId) =>
    api.post(`/api/cases/projekt-entwurf/${entwurfId}/zuordnen`, { user_id: userId }),
  meineEntwuerfe: (userId) =>
    api.get(`/api/cases/meine-entwuerfe/${userId}`),
  premiumInteresse: (entwurfId, falltyp, quelle = 'premium_gate') =>
    api.post('/api/cases/premium-interesse', { entwurf_id: entwurfId, falltyp, quelle }),
  entwurfAktualisieren: (entwurfId, data) =>
    api.put(`/api/cases/projekt-entwurf/${entwurfId}/aktualisieren`, data),
  lagebeurteilungAnfordern: (entwurfId, bundesland) =>
    api.post('/api/cases/lagebeurteilung', { entwurf_id: entwurfId, bundesland }),
  lagebeurteilungStatus: (entwurfId) =>
    api.get(`/api/cases/lagebeurteilung/${entwurfId}`),
  entwurfPdfExport: (entwurfId) =>
    api.get(`/api/cases/entwurf/${entwurfId}/pdf`, { responseType: 'blob' }),
  entwurfVorschauPdf: (entwurfId) =>
    api.get(`/api/cases/entwurf/${entwurfId}/vorschau-pdf`, { responseType: 'blob' }),
  entwurfSzenarioPdf: (entwurfId) =>
    api.get(`/api/cases/entwurf/${entwurfId}/szenario-pdf`, { responseType: 'blob' }),
  entwurfAnschlussoptionen: (entwurfId) =>
    api.get(`/api/cases/entwurf/${entwurfId}/anschlussoptionen`),
  entwurfVertrauenshinweise: (entwurfId) =>
    api.get(`/api/cases/entwurf/${entwurfId}/vertrauenshinweise`),
  // Payment
  stufeCPreis: () => api.get('/api/payments/preis'),
  stufeCCheckout: (entwurfId, originUrl) =>
    api.post('/api/payments/checkout', { entwurf_id: entwurfId, origin_url: originUrl }),
  stufeCStatus: (sessionId) =>
    api.get(`/api/payments/status/${sessionId}`),
  stufeCCancel: (entwurfId) =>
    api.post('/api/payments/cancel', { entwurf_id: entwurfId }),
  // Test-Unlock (intern)
  testUnlockStatus: () =>
    api.get('/api/cases/test-unlock-status'),
  testUnlock: (entwurfId) =>
    api.post(`/api/cases/entwurf/${entwurfId}/test-unlock`),
  testReset: (entwurfId) =>
    api.post(`/api/cases/entwurf/${entwurfId}/test-reset`),
  // Szenario: "Was passiert wenn?"
  szenarioOptionen: (entwurfId, sprache = 'de') =>
    api.get(`/api/cases/entwurf/${entwurfId}/szenario-optionen?sprache=${sprache}`),
  szenarioAusfuehren: (entwurfId, szenarioTyp, sprache = 'de') =>
    api.post(`/api/cases/entwurf/${entwurfId}/szenario`, { szenario_typ: szenarioTyp, sprache }),
};

// ========================================
// SAMMELFALL (FBG Multi-Case)
// ========================================
export const sammelfallAPI = {
  erstellen: (freitext = null, zielgruppe = 'fbg', userId = null) =>
    api.post('/api/sammelfall', { freitext, zielgruppe, user_id: userId }),
  abrufen: (id) => api.get(`/api/sammelfall/${id}`),
  meine: (userId) => api.get(`/api/sammelfall/meine/${userId}`),
  fallHinzufuegen: (id, freitext) =>
    api.post(`/api/sammelfall/${id}/fall`, { freitext }),
  fallEntfernen: (id, fallIndex) =>
    api.delete(`/api/sammelfall/${id}/fall/${fallIndex}`),
  pdfHerunterladen: (id) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${apiBase}/api/sammelfall/${id}/pdf`, '_blank');
  },
  // Ausschreibungen
  ausschreibungErstellen: (data) =>
    api.post('/api/sammelfall/ausschreibung', data),
  offeneAusschreibungen: () =>
    api.get('/api/sammelfall/ausschreibungen/offen'),
  ausschreibungAbrufen: (id) =>
    api.get(`/api/sammelfall/ausschreibung/${id}`),
  bewerben: (data) =>
    api.post('/api/sammelfall/ausschreibung/bewerben', data),
  meineAusschreibungen: (userId) =>
    api.get(`/api/sammelfall/ausschreibungen/meine/${userId}`),
};

// ========================================
// DIENSTLEISTER (P1)
// ========================================
export const dienstleisterAPI = {
  optionen: () => api.get('/api/dienstleister/optionen'),
  profilErstellen: (data) => api.post('/api/dienstleister/profil', data),
  profilAktualisieren: (profilId, data) => api.put(`/api/dienstleister/profil/${profilId}`, data),
  profilLoeschen: (profilId) => api.delete(`/api/dienstleister/profil/${profilId}`),
  profilAbrufen: (profilId) => api.get(`/api/dienstleister/profil/${profilId}`),
  meinProfil: (email) => api.get(`/api/dienstleister/mein-profil/${encodeURIComponent(email)}`),
  referenzen: (profilId) => api.get(`/api/dienstleister/profil/${profilId}/referenzen`),
  referenzHinzufuegen: (profilId, data) => api.post(`/api/dienstleister/profil/${profilId}/referenz`, data),
  bewertungen: (profilId) => api.get(`/api/dienstleister/profil/${profilId}/bewertungen`),
  bewertungAbgeben: (profilId, data) => api.post(`/api/dienstleister/profil/${profilId}/bewertung`, data),
  regionalUebersicht: () => api.get('/api/dienstleister/regional-uebersicht'),
};

export const adminAPI = {
  users: () => api.get('/api/admin/users'),
  deleteUser: (id) => api.delete(`/api/admin/users/${id}`),
  projekte: () => api.get('/api/admin/projekte'),
  deleteProjekt: (id) => api.delete(`/api/admin/projekte/${id}`),
  profile: () => api.get('/api/admin/profile'),
  deleteProfile: (id) => api.delete(`/api/admin/profile/${id}`),
  entwuerfe: () => api.get('/api/admin/entwuerfe'),
  deleteEntwurf: (id) => api.delete(`/api/admin/entwuerfe/${id}`),
  bewertungen: () => api.get('/api/admin/bewertungen'),
  deleteBewertung: (id) => api.delete(`/api/admin/bewertungen/${id}`),
  waldflaechen: () => api.get('/api/admin/waldflaechen'),
  deleteWaldflaeche: (id) => api.delete(`/api/admin/waldflaechen/${id}`),
  faelle: () => api.get('/api/admin/faelle'),
  deleteFall: (id) => api.delete(`/api/admin/faelle/${id}`),
  nachrichten: () => api.get('/api/admin/nachrichten'),
  deleteNachricht: (id) => api.delete(`/api/admin/nachrichten/${id}`),
};

export default api;
